import { Button, Tooltip } from '@dixa/lib-shared-habla';
import type { Conversation } from 'lib-shared-legacy-types';
import debounce from 'lodash/debounce';
import * as React from 'react';

import { ConversationDraft } from 'apis/userStorage';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import {
  buildCopilotActions,
  copilotActionContexts,
} from 'components/copilotActions';
import { CopilotAction } from 'components/copilotDropdown';
import { CopilotPinnedActions } from 'components/copilotPinnedActions';
import { EditorCopilotDropdown } from 'components/editorCopilotDropdown';
import { EditorMentions } from 'components/editorMentions';
import { EditorQuickRepliesDropdown } from 'components/editorQuickRepliesDropdown';
import { ValidationState } from 'components/shared/util/richTextField/components/components/stateBar/stateBar.types';
import {
  ConversationDraftField,
  ConversationDraftContent,
} from 'utils/conversationDrafts';

import { getRequestID } from '../../shared/util/request';
import RichTextField from '../../shared/util/richTextField/components/richTextField';
import styles from '../styles/noteForm.module.scss';

type NoteFormProps = {
  className?: string;
  csid: Conversation['id'];
  agentId: string;
  conversationAddWrapup: (payload: any) => void;
  draft: ConversationDraft | null;
  saveDraft: (merge: Partial<ConversationDraftContent>) => void;
  clearDraft: (fields?: readonly ConversationDraftField[]) => void;
  flushDraft: () => void;
  conversationMarkSeen: (payload: { csid: Conversation['id'] }) => void;
};

type StateT = {
  validation: ValidationState;
  isUploadingAttachments: boolean;
  editorCopilotActions: Record<string, CopilotAction>;
  editorText: string;
};

const DEFAULT_MAX_MESSAGE_LENGTH = 50000;

export default class NoteForm extends React.Component<NoteFormProps, StateT> {
  editorRef: React.RefObject<any>;
  /** What the editor was last filled with programmatically. */
  seededEditorText: string = '';

  constructor(props: NoteFormProps) {
    super(props);
    this.state = {
      validation: { touched: false, valid: false },
      isUploadingAttachments: false,
      editorCopilotActions: buildCopilotActions(copilotActionContexts.note, {
        csid: props.csid?.toString(),
      }),
      editorText: '',
    };
    this.editorRef = React.createRef();

    this.handleTypingEvent = debounce(this.handleTypingEvent.bind(this), 500);
  }

  componentDidMount() {
    this.loadTempValue();
    this.editorRef.current.focus();
  }

  componentDidUpdate(prevProps: NoteFormProps) {
    const { csid } = this.props;
    if (csid !== prevProps.csid) {
      this.saveDraftFromEditor();
      this.props.flushDraft();
      this.loadTempValue();
    }
  }

  componentWillUnmount() {
    this.saveDraftFromEditor();
    this.props.flushDraft();
  }

  loadTempValue() {
    const wrapup = this.props.draft?.wrapup ?? '';
    if (this.editorRef && this.editorRef.current) {
      this.editorRef.current.setText(wrapup);
      this.seededEditorText = this.editorRef.current.getText();
      if (wrapup) {
        this.editorRef.current.moveCursorToEnd();
      }
    }
  }

  /** Stores the note. Debounced by the hook, so safe on every keystroke. */
  saveDraftFromEditor() {
    if (!this.editorRef?.current) return;

    const textContent = this.editorRef.current.getText();
    if (typeof textContent !== 'string') return;

    this.props.saveDraft({ wrapup: textContent.trim() });
  }

  setValidationState = (validation: ValidationState) => {
    this.setState({ validation });
  };

  handleFileIsUploadingChange = (isUploadingAttachments: boolean) => {
    this.setState({ isUploadingAttachments });
  };

  handleSubmit = (): void => {
    const text = this.editorRef.current.getText();

    if (text && typeof text === 'string' && text.trim() !== '') {
      if (text.length > DEFAULT_MAX_MESSAGE_LENGTH) {
        this.setValidationState({
          touched: true,
          valid: false,
          error: { type: 'length' },
        });
        return;
      }

      const { csid, agentId, conversationAddWrapup } = this.props;

      const requestID = getRequestID();

      conversationAddWrapup({
        requestID: requestID,
        csid: csid,
        note: text,
        agentId: agentId,
      });

      this.editorRef.current.setText('');
      this.seededEditorText = this.editorRef.current.getText();
      this.setValidationState({ touched: false, valid: true });
      // Only the note; a reply in progress is a separate draft.
      this.props.clearDraft(['wrapup']);
      this.props.conversationMarkSeen({ csid });
      return;
    }

    this.setValidationState({
      touched: true,
      valid: false,
      error: { type: 'required' },
    });
  };

  handleTypingEvent = (editorText: string) => {
    this.setState({ editorText });

    // Programmatic seeding raises this too; ignore it until the content differs.
    if (editorText === this.seededEditorText) return;
    this.seededEditorText = '';

    this.saveDraftFromEditor();
  };
  render() {
    const tooltipContent = (
      <CommandKeybindingsTooltipContent commandName="richTextEditor:send">
        Add internal note
      </CommandKeybindingsTooltipContent>
    );

    return (
      <div className={styles.form}>
        <div className="conversation-form__editorwrap">
          <RichTextField
            className={`conversation-form__editornew ${styles.editor}`}
            ref={this.editorRef}
            channel="notes"
            placeholder="Type your internal note"
            onChange={this.handleTypingEvent}
            onFileIsUploadingChange={this.handleFileIsUploadingChange}
            onTriggerSend={() => this.handleSubmit()}
            validation={this.state.validation}
            setValidationState={this.setValidationState}
            toolbarAddons={
              <>
                <EditorMentions />
                <EditorQuickRepliesDropdown />
                <EditorCopilotDropdown
                  editorCopilotActions={this.state.editorCopilotActions}
                />
                <CopilotPinnedActions
                  context={copilotActionContexts.note}
                  text={this.state.editorText ?? ''}
                  csid={this.props.csid?.toString()}
                />
              </>
            }
            toolbarEnd={
              <Tooltip content={tooltipContent} wrap="div">
                <Button
                  type="button"
                  disabled={this.state.isUploadingAttachments}
                  onClick={this.handleSubmit}
                >
                  {this.state.isUploadingAttachments ? 'Uploading' : 'Add note'}
                </Button>
              </Tooltip>
            }
          />
        </div>
      </div>
    );
  }
}
